/**
 * Forensic export — investigator queue + new-request flow.
 *
 * Two-person approval: a request sits in `pending_approval` until a
 * second investigator / global admin approves via the panel on the
 * right. Requester ≠ approver is enforced server-side; the UI greys
 * out the approve button on rows you requested.
 */
import { useState } from "react";
import { AlertCircle, CheckCircle2, Clock, Loader2, XCircle } from "lucide-react";

import { ApiError } from "@/lib/apiClient";
import { ENTITIES } from "@/lib/mockData";

import {
  ForensicExport,
  ForensicExportState,
  useApproveForensicExport,
  useForensicExports,
  useRejectForensicExport,
  useRequestForensicExport,
} from "./api";

const STATE_LABEL: Record<ForensicExportState, string> = {
  pending_approval: "Pending approval",
  approved: "Approved — generating",
  complete: "Complete",
  rejected: "Rejected",
  failed: "Generation failed",
};

function StateIcon({ state }: { state: ForensicExportState }) {
  switch (state) {
    case "pending_approval":
      return <Clock size={14} className="text-status-approval" />;
    case "approved":
      return <Loader2 size={14} className="animate-spin text-status-running" />;
    case "complete":
      return <CheckCircle2 size={14} className="text-status-success" />;
    case "rejected":
    case "failed":
      return <XCircle size={14} className="text-status-failed" />;
  }
}

export function ForensicExportScreen() {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showNew, setShowNew] = useState(false);
  const [flash, setFlash] = useState<string | null>(null);

  const { data, isLoading, error, refetch } = useForensicExports();
  const approve = useApproveForensicExport();
  const reject = useRejectForensicExport();

  const items = data?.items ?? [];
  const selected = items.find((x) => x.id === selectedId) ?? null;

  const flashMessage = (msg: string) => {
    setFlash(msg);
    window.setTimeout(() => setFlash((f) => (f === msg ? null : f)), 3500);
  };

  const onApprove = (id: string) => {
    approve.mutate(
      { id },
      {
        onSuccess: () => flashMessage("Export approved — generation queued."),
        onError: (err) => flashMessage(`Approve failed: ${(err as Error).message}`),
      },
    );
  };

  const onReject = (id: string, notes?: string) => {
    reject.mutate(
      { id, notes },
      {
        onSuccess: () => flashMessage("Export rejected."),
        onError: (err) => flashMessage(`Reject failed: ${(err as Error).message}`),
      },
    );
  };

  return (
    <div className="flex h-full flex-col overflow-hidden bg-paper-1">
      <header className="flex items-center justify-between border-b border-paper-line bg-paper-0 px-6 py-4">
        <div>
          <h1 className="text-lg font-semibold text-ink-1">Forensic export</h1>
          <p className="text-xs text-ink-3">
            Two-person approval · patient-level audit evidence ·{" "}
            {items.filter((x) => x.state === "pending_approval").length} pending
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowNew(true)}
          className="rounded-md bg-ink-1 px-3 py-1.5 text-sm text-paper-0 hover:bg-ink-0"
        >
          New request
        </button>
      </header>

      <div className="flex flex-1 overflow-hidden">
        <section className="flex-1 overflow-y-auto border-r border-paper-line">
          {isLoading && (
            <div className="flex items-center gap-2 p-6 text-ink-3">
              <Loader2 className="animate-spin" size={18} /> Loading exports…
            </div>
          )}
          {error && !isLoading && (
            <ErrorCard
              message={(error as Error).message}
              correlationId={error instanceof ApiError ? error.correlationId : null}
              onRetry={() => refetch()}
            />
          )}
          {!isLoading && !error && items.length === 0 && (
            <div className="p-8 text-center text-sm text-ink-3">
              No forensic exports yet. Click <b>New request</b> to start one.
            </div>
          )}
          {items.length > 0 && (
            <ul className="divide-y divide-paper-line">
              {items.map((item) => (
                <li
                  key={item.id}
                  className={`cursor-pointer px-4 py-3 hover:bg-paper-1 ${
                    selectedId === item.id ? "bg-paper-1" : ""
                  }`}
                  onClick={() => setSelectedId(item.id)}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 text-sm font-medium text-ink-1">
                        <StateIcon state={item.state} />
                        <span className="truncate">
                          {item.reason.slice(0, 60)}
                          {item.reason.length > 60 ? "…" : ""}
                        </span>
                      </div>
                      <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-ink-3">
                        <span>{STATE_LABEL[item.state]}</span>
                        <span>·</span>
                        <span>{new Date(item.requestedAt).toLocaleString()}</span>
                        {item.entityCode && (
                          <>
                            <span>·</span>
                            <span>{item.entityCode}</span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>

        <aside className="w-96 overflow-y-auto bg-paper-0 p-5">
          {selected ? (
            <ExportDetail
              row={selected}
              onApprove={() => onApprove(selected.id)}
              onReject={(notes) => onReject(selected.id, notes)}
              approving={approve.isPending}
              rejecting={reject.isPending}
            />
          ) : (
            <div className="text-sm text-ink-3">Select a request to view details.</div>
          )}
        </aside>
      </div>

      {showNew && (
        <NewRequestModal
          onClose={() => setShowNew(false)}
          onDone={(msg) => {
            setShowNew(false);
            flashMessage(msg);
          }}
        />
      )}

      {flash && (
        <div
          role="status"
          className="fixed bottom-6 right-6 rounded-md bg-ink-1 px-4 py-2 text-sm text-paper-0 shadow-lg"
        >
          {flash}
        </div>
      )}
    </div>
  );
}

function ExportDetail(props: {
  row: ForensicExport;
  onApprove: () => void;
  onReject: (notes?: string) => void;
  approving: boolean;
  rejecting: boolean;
}) {
  const { row } = props;
  const isPending = row.state === "pending_approval";
  return (
    <div className="space-y-3 text-sm">
      <div>
        <div className="text-xs text-ink-3">Reason</div>
        <p className="text-ink-1">{row.reason}</p>
      </div>
      <Row label="State" value={STATE_LABEL[row.state]} />
      <Row label="Window" value={`${new Date(row.startAt).toLocaleString()} → ${new Date(row.endAt).toLocaleString()}`} />
      <Row label="Entity" value={row.entityCode ?? "all in scope"} />
      <Row label="Requested by" value={row.requestedBy} mono />
      <Row label="Requested at" value={new Date(row.requestedAt).toLocaleString()} />
      {row.approvedBy && <Row label="Resolved by" value={row.approvedBy} mono />}
      {row.approvedAt && <Row label="Resolved at" value={new Date(row.approvedAt).toLocaleString()} />}
      {row.failureReason && <Row label="Note" value={row.failureReason} />}
      {row.signaturePresent && <Row label="Blob signature" value="present" />}
      {row.blobRef && (
        <Row label="Blob ref" value={row.blobRef} mono />
      )}

      {isPending && (
        <div className="mt-4 space-y-2 border-t border-paper-line pt-4">
          <div className="text-xs text-ink-3">
            Approval is two-person. Requester ≠ approver is enforced server-side;
            you'll get a 409 if you try to self-approve.
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={props.onApprove}
              disabled={props.approving}
              className="flex-1 rounded-md bg-ink-1 px-3 py-1.5 text-sm text-paper-0 disabled:opacity-50"
            >
              {props.approving ? "Approving…" : "Approve"}
            </button>
            <button
              type="button"
              onClick={() => {
                const note = window.prompt("Reason for rejection?") ?? undefined;
                props.onReject(note || undefined);
              }}
              disabled={props.rejecting}
              className="rounded-md border border-paper-line bg-paper-0 px-3 py-1.5 text-sm"
            >
              Reject
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function Row(props: { label: string; value: string; mono?: boolean }) {
  return (
    <div>
      <div className="text-xs text-ink-3">{props.label}</div>
      <div className={`text-ink-1 ${props.mono ? "break-all font-mono text-xs" : ""}`}>
        {props.value}
      </div>
    </div>
  );
}

function ErrorCard(props: {
  message: string;
  correlationId: string | null;
  onRetry: () => void;
}) {
  return (
    <div className="m-6 flex items-start gap-3 rounded-md border border-status-failed/30 bg-status-failed/5 p-4 text-sm">
      <AlertCircle className="text-status-failed" size={18} />
      <div>
        <div className="font-medium text-ink-1">Couldn't load exports.</div>
        <div className="text-ink-3">{props.message}</div>
        {props.correlationId && (
          <div className="mt-1 font-mono text-[10px] text-ink-3">{props.correlationId}</div>
        )}
        <button
          type="button"
          onClick={props.onRetry}
          className="mt-2 rounded-md border border-paper-line bg-paper-0 px-2 py-1 text-xs"
        >
          Retry
        </button>
      </div>
    </div>
  );
}

function NewRequestModal(props: { onClose: () => void; onDone: (msg: string) => void }) {
  const today = new Date().toISOString().slice(0, 10);
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [entityCode, setEntityCode] = useState<string>("");
  const [reason, setReason] = useState("");
  const req = useRequestForensicExport();

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (reason.trim().length < 10) {
      props.onDone("Reason must be at least 10 chars.");
      return;
    }
    req.mutate(
      {
        start_date: startDate,
        end_date: endDate,
        entity_code: entityCode || undefined,
        reason,
      },
      {
        onSuccess: () => props.onDone("Export request filed. Pending second approval."),
        onError: (err) => props.onDone(`Failed: ${(err as Error).message}`),
      },
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink-1/30 p-4">
      <div className="w-full max-w-md rounded-lg border border-paper-line bg-paper-0 p-5 shadow-xl">
        <h3 className="mb-4 text-sm font-semibold text-ink-1">New forensic export</h3>
        <form onSubmit={submit} className="space-y-3 text-sm">
          <label className="block text-xs text-ink-3">
            Start date
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              required
              className="mt-1 w-full rounded-md border border-paper-line bg-paper-0 px-2 py-1.5 text-ink-1"
            />
          </label>
          <label className="block text-xs text-ink-3">
            End date
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              required
              className="mt-1 w-full rounded-md border border-paper-line bg-paper-0 px-2 py-1.5 text-ink-1"
            />
          </label>
          <label className="block text-xs text-ink-3">
            Entity (optional)
            <select
              value={entityCode}
              onChange={(e) => setEntityCode(e.target.value)}
              className="mt-1 w-full rounded-md border border-paper-line bg-paper-0 px-2 py-1.5 text-ink-1"
            >
              <option value="">All in scope</option>
              {ENTITIES.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.name}
                </option>
              ))}
            </select>
          </label>
          <label className="block text-xs text-ink-3">
            Reason (HIPAA audit trail — min 10 chars)
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              required
              minLength={10}
              rows={3}
              className="mt-1 w-full rounded-md border border-paper-line bg-paper-0 px-2 py-1.5 text-ink-1"
            />
          </label>
          {req.error && (
            <div className="rounded-md bg-status-failed/10 p-2 text-xs text-status-failed">
              {(req.error as Error).message}
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={props.onClose}
              className="rounded-md border border-paper-line bg-paper-0 px-3 py-1.5 text-sm"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={req.isPending}
              className="rounded-md bg-ink-1 px-3 py-1.5 text-sm text-paper-0 disabled:opacity-50"
            >
              {req.isPending ? "Filing…" : "File request"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
